import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PostSignupPushNudge from "./PostSignupPushNudge";

/* Spotlight tour — navigates the user through real pages, anchoring a tooltip
 * to a real element on each page. Persists completion in localStorage so it
 * only runs once. Skippable at any time.
 *
 * v3 (Feb 2026) — rewritten for: Traditions lexicon, Oracle (deeper+compare),
 * Weekly community feed, bell-based notifications, the Messages inbox, the
 * You-tab self-profile preview + bio.
 */

const KEY = "cor_tour_v4";

/* Each step:
 *   path           — route to navigate to before the step renders
 *   anchors        — array of selectors; the first one that matches is used.
 *                    Lets a single step adapt to mobile vs desktop layouts.
 *   anchor         — convenience shortcut for single-selector steps
 *   label, title, body
 *   scrollIntoView — scroll the matched anchor into view first
 */
const STEPS = [
  {
    path: "/",
    anchor: null,
    label: "Welcome",
    title: "Welcome to Cor·relate.",
    body: "An archive about the patterns that show up across traditions — religion, esoterica, history, science, the heart. A 45-second walk so you know where everything lives.",
  },
  {
    path: "/traditions",
    anchors: ['[data-testid="traditions-page"] h1', '[data-testid="traditions-page"]'],
    label: "1 of 8 — Traditions",
    title: "The lexicon of traditions.",
    body: "Stoicism, Sufism, Vedanta, Quantum Physics, the Law of One — every tradition gets a page with its core insight, vocabulary, and where it converges with the others. Pick one to wander.",
    scrollIntoView: true,
  },
  {
    path: "/traditions/sufism",
    anchors: ['[data-testid="tradition-ask-oracle-btn"]', '[data-testid="tradition-ask-oracle"]'],
    label: "2 of 8 — Ask · Compare",
    title: "Go deeper, or compare two traditions.",
    body: "From any tradition page you can ask the Oracle to walk you through it, or compare it side-by-side with another (Sufism ↔ Stoicism, Kabbalah ↔ Quantum…). Both buttons jump straight to a streaming answer.",
    scrollIntoView: true,
  },
  {
    path: "/oracle",
    anchors: ['[data-testid="oracle-input"]'],
    label: "3 of 8 — The Oracle",
    title: "Ask anything — even the strange.",
    body: "A scholar-mystic that draws from many traditions. Use the Deeper and Compare modes for guided framings, save any reply, or share the best ones to the weekly feed.",
    scrollIntoView: true,
  },
  {
    path: "/weekly",
    anchors: ['[data-testid="nav-compose"]'],
    label: "4 of 8 — Write your own",
    title: "A revelation of your own? Just post it.",
    body: "The Oracle isn't the only voice here. Tap the “+” in the top bar any time to write a short, blog-style post — an observation, a resonance, a question — straight to the Weekly feed. No tradition or framing required.",
  },
  {
    path: "/weekly",
    anchors: ['[data-testid="weekly-search"]', '[data-testid="weekly-page"]'],
    label: "5 of 8 — Weekly",
    title: "What other readers are surfacing.",
    body: "A community feed of correlations readers share each week. Bump the ones that move you, leave threaded comments, search by topic or @handle, and tap a name to read their shelf.",
    scrollIntoView: true,
  },
  {
    path: "/weekly",
    anchors: ['[data-testid="notif-bell"]'],
    label: "6 of 8 — The bell",
    title: "All notifications live here.",
    body: "Follows, replies, share-backs, and new correlations from readers you follow — all surface behind the bell at the top. Bell + push only; DMs ping here too but never via email.",
  },
  {
    path: "/messages",
    anchors: ['[data-testid="header-compose-cta"]', '[data-testid="messages-page"]'],
    label: "7 of 8 — Private letters",
    title: "Direct messages with other readers.",
    body: "Quiet 1-on-1 conversations. Tap “Message” on any byline, or use “+ New message” to find someone by @handle or name. You can close your inbox any time in Settings.",
    scrollIntoView: true,
  },
  {
    path: "/settings#you",
    anchors: ['[data-testid="my-profile-preview"]', '[data-testid="bio-section"]'],
    label: "8 of 8 — Your shelf",
    title: "Write a bio. See how others see you.",
    body: "The You tab is your archive: a bio, follower counts, pins, reactions, contributions, and Oracle saves. Claim a public @handle and turn on the Public reading shelf when you're ready to be found.",
    scrollIntoView: true,
  },
  {
    path: "/traditions",
    anchor: null,
    label: "Welcome in",
    title: "Read kindly. Write quietly.",
    body: "Everything's labelled. Every reading is provisional. Pick the doorway that calls — and if you ever want this walk again, you'll find it in Settings → Replay the guided walk.",
  },
];

export default function GuidedTour() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  // After the tour closes (either finished or skipped), chain into the
  // "enable notifications" nudge. This is the "on by default" onboarding
  // beat — one warm ask, after the seeker has already seen what the app
  // does, at the moment where saying yes actually makes sense.
  const [pushNudgeOpen, setPushNudgeOpen] = useState(false);

  // Decide on first run whether to open. Run only after auth is settled.
  useEffect(() => {
    if (loading || !user) return;
    const done = localStorage.getItem(KEY);
    if (done === "1") return;
    // Defer slightly so the landing page renders first.
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [user, loading]);

  // Navigate when the step's path differs from the current location.
  useEffect(() => {
    if (!open) return;
    const step = STEPS[idx];
    if (!step) return;
    const [path, hash = ""] = step.path.split("#");
    if (location.pathname !== path || (hash && location.hash !== `#${hash}`)) {
      navigate(step.path);
    }
  }, [open, idx, location.pathname, location.hash, navigate]);

  // After the step renders on the right path, find + measure the first anchor that exists.
  useLayoutEffect(() => {
    if (!open) { setAnchorRect(null); return; }
    const step = STEPS[idx];
    if (!step) { setAnchorRect(null); return; }
    const selectors = step.anchors || (step.anchor ? [step.anchor] : []);
    if (selectors.length === 0) { setAnchorRect(null); return; }
    const stepPath = step.path.split("#")[0];
    if (location.pathname !== stepPath) { setAnchorRect(null); return; }

    let cancelled = false;

    const measure = () => {
      let el = null;
      for (const sel of selectors) {
        el = document.querySelector(sel);
        if (el) break;
      }
      if (!el) return false;
      if (step.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      const rect = el.getBoundingClientRect();
      if (!cancelled) setAnchorRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      return true;
    };

    // Poll a few times — the target may not exist yet (data fetches still resolving).
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (measure()) return;
      tries += 1;
      if (tries < 25) setTimeout(tick, 200);
      else setAnchorRect(null); // give up — render centered
    };
    tick();

    const onUpdate = () => measure();
    window.addEventListener("resize", onUpdate);
    window.addEventListener("scroll", onUpdate, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("scroll", onUpdate, true);
    };
  }, [open, idx, location.pathname]);

  const finish = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
    // Small delay so the tour's fade-out completes before the nudge fades
    // in — feels like one connected onboarding beat rather than a jump.
    setTimeout(() => setPushNudgeOpen(true), 300);
  };

  const next = () => setIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  if (!open) {
    // The tour itself has closed. The push nudge, when queued, needs to
    // still render — so we keep the component alive.
    return (
      <PostSignupPushNudge
        open={pushNudgeOpen}
        onDone={() => setPushNudgeOpen(false)}
      />
    );
  }
  const step = STEPS[idx];
  const isFirst = idx === 0;
  const isLast = idx === STEPS.length - 1;

  return createPortal(
    <div data-testid="guided-tour" className="fixed inset-0 z-[70] pointer-events-none">
      <SpotlightBackdrop rect={anchorRect} onClick={finish} />
      <TooltipCard
        rect={anchorRect}
        step={step}
        isFirst={isFirst}
        isLast={isLast}
        currentIdx={idx}
        total={STEPS.length}
        onNext={isLast ? finish : next}
        onPrev={prev}
        onSkip={finish}
      />
    </div>,
    document.body,
  );
}

function SpotlightBackdrop({ rect, onClick }) {
  if (!rect) {
    return (
      <div
        data-testid="tour-backdrop"
        onClick={onClick}
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm pointer-events-auto transition-opacity duration-300"
      />
    );
  }
  const pad = 12;
  const top = Math.max(0, rect.top - pad);
  const left = Math.max(0, rect.left - pad);
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;

  return (
    <>
      <div onClick={onClick} className="absolute left-0 top-0 right-0 bg-foreground/40 backdrop-blur-[2px] pointer-events-auto" style={{ height: top }} />
      <div onClick={onClick} className="absolute left-0 right-0 bottom-0 bg-foreground/40 backdrop-blur-[2px] pointer-events-auto" style={{ top: top + height }} />
      <div onClick={onClick} className="absolute left-0 bg-foreground/40 backdrop-blur-[2px] pointer-events-auto" style={{ top, left: 0, width: left, height }} />
      <div onClick={onClick} className="absolute right-0 bg-foreground/40 backdrop-blur-[2px] pointer-events-auto" style={{ top, left: left + width, right: 0, height }} />
      <div
        className="absolute pointer-events-none ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background/0 rounded-sm"
        style={{ top, left, width, height }}
      />
    </>
  );
}

function TooltipCard({ rect, step, isFirst, isLast, currentIdx, total, onNext, onPrev, onSkip }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });

  useLayoutEffect(() => {
    if (!rect) {
      setPos({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }
    const card = ref.current;
    if (!card) return;
    const cardH = card.offsetHeight;
    const cardW = card.offsetWidth;
    const gap = 18;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = rect.top + rect.height + gap;
    if (top + cardH + 16 > vh) {
      top = Math.max(16, rect.top - cardH - gap);
    }
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(16, Math.min(left, vw - cardW - 16));
    setPos({ top, left, transform: "none" });
  }, [rect]);

  return (
    <div
      ref={ref}
      data-testid="tour-card"
      className="absolute paper p-6 shadow-2xl pointer-events-auto w-[360px] max-w-[calc(100vw-32px)]"
      style={pos}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[hsl(var(--primary))]" />
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">{step.label}</span>
        </div>
        <button data-testid="tour-close" onClick={onSkip} className="text-muted-foreground hover:text-foreground" aria-label="Close tour">
          <X size={16} />
        </button>
      </div>

      <h2 className="serif text-2xl text-foreground leading-tight">{step.title}</h2>
      <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed">{step.body}</p>

      <div className="mt-6 flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <span
            // STEPS.length is fixed at module load and the tour never
            // reorders, so the step label at position i is a stable ID.
            key={STEPS[i]?.label || `dot-${i}`}
            className={`block h-[2px] flex-1 transition-colors ${i <= currentIdx ? "bg-[hsl(var(--primary))]" : "bg-border"}`}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        {!isFirst ? (
          <button data-testid="tour-prev" onClick={onPrev} className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ChevronLeft size={12} /> Back
          </button>
        ) : (
          <button data-testid="tour-skip" onClick={onSkip} className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground link-underline">
            Skip the tour
          </button>
        )}
        <button data-testid="tour-next" onClick={onNext} className="btn-gold inline-flex items-center gap-2">
          {isLast ? "Enter the archive" : "Next"}
          {!isLast && <ChevronRight size={12} />}
        </button>
      </div>
    </div>
  );
}
